import React, { useEffect, useRef, useState } from 'react';
import type { MapBlock, MapPoint } from '../../lib/api/geologist';
import type { GeoUiLang } from './geoWorkspaceI18n';
import {
  GOOGLE_MAPS_AUTH_FAILURE_EVENT,
  GOOGLE_MAPS_MAP_ID,
  isGoogleMapsConfigured,
  loadGoogleMaps,
  makeMapMarker,
  type GoogleAdvancedMarker,
} from './googleMaps';

// P7.2 §8 — BoreholeMap. Renders ONLY verified WGS84 / approved-CRS-transformed
// coordinates. Never geocodes, routes, or guesses a position. Required states:
// loading, ready, no-coordinates, unknown-CRS-blocked, API-key-missing, transform-error.

const box: React.CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 8, padding: 20,
  background: 'var(--bg-card-inset)', textAlign: 'center', color: 'var(--ink-secondary)',
};

const MAP_COPY = {
  ru: { report: 'отчёт', original: 'Координаты из отчёта', printed: 'Печатная запись X/Y', normalized: 'Нормализованные X/Y', sourceCrs: 'CRS источника', source: 'Источник', page: 'стр.', table: 'табл.', parsed: 'Координаты, преобразованные системой', samples: 'Пробы', intervals: 'Интервалов', review: 'Проверка', loading: 'Загрузка карты…', none: 'Координаты скважин не найдены в активных источниках.', transformError: 'Ошибка преобразования координат.', noKey: 'Ключ Google Maps не настроен.', provisional: 'Приблизительная привязка по одной точке — ориентация не подтверждена.', ready: 'Координаты преобразованы в WGS84 и готовы к показу.', below: 'Ниже — список скважин.', unavailable: 'Интерактивная карта Google недоступна.', transformed: 'преобразованные', approximate: 'приблизительные', attachment: 'Привязка', residual: 'невязка', open3d: 'Открыть индивидуальную 3D-модель' },
  hy: { report: 'հաշվետվություն', original: 'Կոորդինատները հաշվետվությունից', printed: 'Տպագիր X/Y գրառում', normalized: 'Նորմալացված X/Y', sourceCrs: 'Աղբյուրի CRS', source: 'Աղբյուր', page: 'էջ', table: 'աղ.', parsed: 'Համակարգով փոխակերպված կոորդինատներ', samples: 'Նմուշներ', intervals: 'Միջակայքեր', review: 'Ստուգում', loading: 'Քարտեզը բեռնվում է…', none: 'Ակտիվ աղբյուրներում հորատանցքերի կոորդինատներ չեն գտնվել։', transformError: 'Կոորդինատների փոխակերպման սխալ։', noKey: 'Google Maps-ի բանալին կազմաձևված չէ։', provisional: 'Մեկ կետով մոտավոր կապակցում․ ուղղությունը հաստատված չէ։', ready: 'Կոորդինատները փոխակերպված են WGS84-ի և պատրաստ են ցուցադրման։', below: 'Ստորև հորատանցքերի ցանկն է։', unavailable: 'Google-ի ինտերակտիվ քարտեզը հասանելի չէ։', transformed: 'փոխակերպված', approximate: 'մոտավոր', attachment: 'Կապակցում', residual: 'շեղում', open3d: 'Բացել անհատական 3D մոդելը' },
  en: { report: 'report', original: 'Coordinates from report', printed: 'Printed X/Y', normalized: 'Normalised X/Y', sourceCrs: 'Source CRS', source: 'Source', page: 'p.', table: 'table', parsed: 'Coordinates transformed by the system', samples: 'Samples', intervals: 'Intervals', review: 'Review', loading: 'Loading map…', none: 'No borehole coordinates were found in the active sources.', transformError: 'Coordinate transformation error.', noKey: 'Google Maps key is not configured.', provisional: 'Approximate single-point georeference; orientation is not confirmed.', ready: 'Coordinates are transformed to WGS84 and ready to display.', below: 'The borehole list is shown below.', unavailable: 'The interactive Google map is unavailable.', transformed: 'transformed', approximate: 'approximate', attachment: 'Georeference', residual: 'residual', open3d: 'Open individual 3D model' },
};

const PointDetail: React.FC<{
  p: MapPoint; lang: GeoUiLang; onOpen?: (point: MapPoint) => void;
}> = ({ p, lang, onOpen }) => {
  const copy = MAP_COPY[lang];
  return (
  <div style={{ fontSize: 13, lineHeight: 1.7 }}>
    <div style={{ fontWeight: 600, color: 'var(--ink-primary)' }}>{p.borehole_code}</div>
    {p.report_year != null && <div style={{ color: 'var(--ink-tertiary)' }}>{copy.report} {p.report_year}</div>}

    <div style={{ fontWeight: 600, color: 'var(--ink-secondary)', marginTop: 4 }}>{copy.original}</div>
    {(p.original_x_text || p.original_y_text) && (
      <div>{copy.printed}: <code>{p.original_x_text ?? '—'}, {p.original_y_text ?? '—'}</code></div>
    )}
    <div>{copy.normalized}: <code>{p.x_original ?? '—'}, {p.y_original ?? '—'}</code>{p.z_original != null && <> · Z {p.z_original}</>}</div>
    <div>{copy.sourceCrs}: {p.source_crs || '—'}</div>
    {(p.source_page || p.source_table) && <div>{copy.source}: {copy.page} {p.source_page || '—'} · {copy.table} {p.source_table || '—'}</div>}

    <div style={{ fontWeight: 600, color: 'var(--ink-secondary)', marginTop: 4 }}>{copy.parsed}</div>
    <div>WGS84: <code>{p.latitude?.toFixed(6)}, {p.longitude?.toFixed(6)}</code></div>

    {p.linked_samples.length > 0 && <div style={{ marginTop: 4 }}>{copy.samples}: {p.linked_samples.join(', ')}</div>}
    {p.depth_intervals.length > 0 && <div>{copy.intervals}: {p.depth_intervals.length}</div>}
    <div>{copy.review}: {p.verification_status}</div>
    {onOpen && p.record_key && (
      <button
        type="button"
        className="geo-map-open-3d"
        onClick={() => onOpen(p)}
      >
        {copy.open3d}
      </button>
    )}
  </div>
  );
};

const FallbackTable: React.FC<{ points: MapPoint[]; lang: GeoUiLang }> = ({ points, lang }) => (
  <div style={{ marginTop: 12, textAlign: 'left', maxHeight: 240, overflow: 'auto' }}>
    {points.map((p) => (
      <div key={p.borehole_code} style={{ borderTop: '1px solid var(--border)', padding: '8px 0' }}>
        <PointDetail p={p} lang={lang} />
      </div>
    ))}
  </div>
);

const BoreholeMap: React.FC<{
  block?: MapBlock | null; loading?: boolean; lang?: GeoUiLang;
  onOpenPoint?: (point: MapPoint) => void;
}> = ({ block, loading, lang = 'ru', onOpenPoint }) => {
  const copy = MAP_COPY[lang];
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);
  const [selected, setSelected] = useState<MapPoint | null>(null);

  const ready = block?.status === 'ready';
  const hasKey = isGoogleMapsConfigured;

  useEffect(() => {
    if (!ready || !hasKey || !mapRef.current || !block) return;
    let cancelled = false;
    const markers: GoogleAdvancedMarker[] = [];
    const authFailure = () => {
      if (cancelled) return;
      cancelled = true;
      setMapError(true);
    };
    window.addEventListener(GOOGLE_MAPS_AUTH_FAILURE_EVENT, authFailure);
    loadGoogleMaps().then(async (g) => {
      if (cancelled || !mapRef.current) return;
      const pts = block.points.filter((p) => p.latitude != null && p.longitude != null);
      if (!pts.length) return;
      const map = new g.maps.Map(mapRef.current, {
        center: { lat: pts[0].latitude, lng: pts[0].longitude }, zoom: 12,
        mapId: GOOGLE_MAPS_MAP_ID, mapTypeId: 'hybrid', mapTypeControl: true,
        streetViewControl: false, fullscreenControl: true, scaleControl: true,
        clickableIcons: false, gestureHandling: 'cooperative',
      });
      const { AdvancedMarkerElement } = await g.maps.importLibrary('marker');
      if (cancelled) return;
      const bounds = new g.maps.LatLngBounds();
      pts.forEach((p) => {
        const pos = { lat: p.latitude as number, lng: p.longitude as number };
        const marker = new AdvancedMarkerElement({
          position: pos,
          map,
          title: p.borehole_code,
          content: makeMapMarker(
            p.borehole_code,
            block.provisional ? 'provisional' : 'verified',
            pts.length > 80,
          ),
          gmpClickable: true,
        });
        marker.addEventListener('gmp-click', () => setSelected(p));
        markers.push(marker);
        bounds.extend(pos);
      });
      if (pts.length > 1) map.fitBounds(bounds, 48);
    }).catch(() => { if (!cancelled) setMapError(true); });
    return () => {
      cancelled = true;
      markers.forEach((marker) => {
        try { marker.map = null; } catch { /* third-party teardown is best-effort */ }
      });
      window.removeEventListener(GOOGLE_MAPS_AUTH_FAILURE_EVENT, authFailure);
    };
  }, [ready, hasKey, block]);

  if (loading) return <div data-testid="map-loading" style={box}>{copy.loading}</div>;
  if (!block || block.status === 'no_coordinates')
    return <div data-testid="map-no-coordinates" style={box}>{copy.none}</div>;

  if (block.status === 'unknown_crs_blocked')
    return (
      <div data-testid="map-crs-blocked" style={{ ...box, borderLeft: '3px solid var(--amber)', color: 'var(--amber)', textAlign: 'left' }}>
        {block.blocked_reason_user_text}
      </div>
    );

  if (block.status === 'transformation_error')
    return (
      <div data-testid="map-transform-error" style={{ ...box, borderLeft: '3px solid var(--danger)', color: 'var(--danger)', textAlign: 'left' }}>
        {block.blocked_reason_user_text || copy.transformError}
      </div>
    );

  // status === 'ready'
  if (!hasKey)
    return (
      <div data-testid="map-api-key-missing" style={{ ...box, borderLeft: '3px solid var(--amber)', textAlign: 'left' }}>
        <div style={{ color: 'var(--amber)', fontWeight: 600 }}>{copy.noKey}</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          {block.provisional
            ? (block.provisional_note || copy.provisional)
            : copy.ready} {copy.below}
        </div>
        <FallbackTable points={block.points} lang={lang} />
      </div>
    );
  if (mapError)
    return (
      <div data-testid="map-transform-error"
        style={{ ...box, borderLeft: `3px solid ${block.provisional ? 'var(--amber)' : 'var(--danger)'}`, textAlign: 'left' }}>
        {block.provisional && block.provisional_note && (
          <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 8 }}>⚠ {block.provisional_note}</div>
        )}
        <div style={{ color: block.provisional ? 'var(--amber)' : 'var(--danger)' }}>
          {copy.unavailable} {copy.below} ({block.provisional ? copy.approximate : copy.transformed})
        </div>
        <FallbackTable points={block.points} lang={lang} />
      </div>
    );

  return (
    <div data-testid="map-ready">
      {block.provisional && (
        <div data-testid="map-provisional-banner" style={{ fontSize: 12, color: 'var(--amber)',
          background: 'var(--amber-bg)', border: '1px solid var(--amber)', borderRadius: 6,
          padding: '8px 10px', marginBottom: 8 }}>
          ⚠ {block.provisional_note || copy.provisional}
        </div>
      )}
      {block.source_crs_label && (
        <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginBottom: 6 }}>
          {block.provisional ? copy.attachment : 'CRS'}: {block.source_crs_label}
          {block.residual_error_m != null && <> · {copy.residual} ≈ {block.residual_error_m} m</>}
          {block.library && <> · {block.library}</>}
        </div>
      )}
      <div ref={mapRef} style={{ height: 380, borderRadius: 8, border: '1px solid var(--border)' }} />
      {selected && (
        <div style={{ marginTop: 10, padding: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)' }}>
          <PointDetail p={selected} lang={lang} onOpen={onOpenPoint} />
        </div>
      )}
    </div>
  );
};

export default BoreholeMap;
